import { motion, useReducedMotion } from "framer-motion";
import { forwardRef } from "react";

import {
  type THeaderMenuToggleIconElement,
  type THeaderMenuToggleIconProps,
} from "@/ui/components/navigation/Header/Header.config";
import { cn } from "@/ui/lib/utils";

const HEADER_MENU_TOGGLE_TRANSITION_TIMES = [ 0, 0.5, 1 ];
const HEADER_MENU_TOGGLE_DURATION_SECONDS = 0.4;

export const HeaderMenuToggleIcon = forwardRef<
  THeaderMenuToggleIconElement,
  THeaderMenuToggleIconProps
>(({ className, open, ...props }, ref) => {
  const shouldReduceMotion = useReducedMotion();
  const transition = {
    duration: shouldReduceMotion ? 0 : HEADER_MENU_TOGGLE_DURATION_SECONDS,
    ease: "easeInOut" as const,
    times: HEADER_MENU_TOGGLE_TRANSITION_TIMES,
  };

  return (
    <svg
      {...props}
      ref={ref}
      aria-hidden="true"
      data-component="HeaderMenuToggleIcon"
      data-state={open ? "open" : "closed"}
      xmlns="http://www.w3.org/2000/svg"
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      className={cn(className)}
    >
      <motion.rect
        width="44"
        height="44"
        stroke="none"
        initial={false}
        animate={{ opacity: open ? [ 1, 1, 0 ] : [ 0, 1, 1 ] }}
        transition={transition}
      />
      <motion.line
        initial={false}
        animate={open ? {
          x1: [ 13, 12.625, 14.4142 ],
          y1: [ 15, 21.375, 13 ],
          x2: [ 30, 31.375, 29.9706 ],
          y2: [ 15, 21.375, 28.5563 ],
          strokeWidth: [ 2, 1.25, 2 ],
        } : {
          x1: [ 14.4142, 12.625, 13 ],
          y1: [ 13, 21.375, 15 ],
          x2: [ 29.9706, 31.375, 30 ],
          y2: [ 28.5563, 21.375, 15 ],
          strokeWidth: [ 2, 1.25, 2 ],
        }}
        transition={transition}
      />
      <motion.line
        initial={false}
        animate={open ? {
          x1: [ 13, 13, 14 ],
          y1: [ 21, 21, 28.5564 ],
          x2: [ 30, 31, 29.5563 ],
          y2: [ 21, 21, 13 ],
          strokeWidth: [ 2, 2, 2 ],
        } : {
          x1: [ 14, 13, 13 ],
          y1: [ 28.5564, 21, 21 ],
          x2: [ 29.5563, 31, 30 ],
          y2: [ 13, 21, 21 ],
          strokeWidth: [ 2, 2, 2 ],
        }}
        transition={transition}
      />
      <motion.line
        initial={false}
        animate={open ? {
          x1: [ 13, 12.625, 14.4142 ],
          y1: [ 27, 21.375, 13 ],
          x2: [ 30, 31.375, 29.9706 ],
          y2: [ 27, 21.375, 28.5563 ],
          strokeWidth: [ 2, 1.25, 2 ],
          opacity: [ 1, 1, 0 ],
        } : {
          x1: [ 14.4142, 12.625, 13 ],
          y1: [ 13, 21.375, 27 ],
          x2: [ 29.9706, 31.375, 30 ],
          y2: [ 28.5563, 21.375, 27 ],
          strokeWidth: [ 2, 1.25, 2 ],
          opacity: [ 0, 1, 1 ],
        }}
        transition={transition}
      />
    </svg>
  );
});

HeaderMenuToggleIcon.displayName = "HeaderMenuToggleIcon";
